import React from "react"

import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded"
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded"
import SearchRoundedIcon from "@mui/icons-material/SearchRounded"

import { usePrefersReducedMotion } from "../../../../../clux-ui/hooks"
import { CLUE_EXIT_DURATION_MS } from "../../config/constants"
import { DetectiveCluxItem, Level } from "../../config/interfaces"
import { useExitTransition } from "../../useExitTransition"
import ClueCard from "../ClueCard"

import "./style.scss"

interface PiocheProps {
  currentClue: DetectiveCluxItem | null
  level: Level
  /** Position (à partir de 0) de l'indice affiché parmi les indices piochés */
  clueIndex: number | null
  drawnCount: number
  totalClues: number
  canDraw: boolean
  canShowPrevious: boolean
  canShowNext: boolean
  /** Il ne reste qu'une tuile par famille : la validation remplace la pioche */
  canConfirm: boolean
  onDraw: () => void
  onShowPrevious: () => void
  onShowNext: () => void
  onConfirm: () => void
}

/**
 * Pioche d'indices : un paquet de cartes à retourner une par une.
 * L'indice tiré s'affiche à côté du paquet ; des flèches permettent de revoir les indices déjà piochés.
 */
const Pioche = ({
  currentClue,
  level,
  clueIndex,
  drawnCount,
  totalClues,
  canDraw,
  canShowPrevious,
  canShowNext,
  canConfirm,
  onDraw,
  onShowPrevious,
  onShowNext,
  onConfirm,
}: PiocheProps) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const { displayedValue: displayedClue, isLeaving } = useExitTransition(
    currentClue,
    prefersReducedMotion ? 0 : CLUE_EXIT_DURATION_MS,
  )
  const clueNumber = (clueIndex ?? 0) + 1

  return (
    <div className="dc-pioche">
      {canConfirm ? (
        <button
          type="button"
          className="dc-pioche__confirm"
          onClick={onConfirm}
        >
          <CheckCircleRoundedIcon className="dc-pioche__confirm-icon" />
          <span>Valider mon enquête</span>
        </button>
      ) : (
        <button
          type="button"
          className="dc-pioche__deck"
          onClick={onDraw}
          disabled={!canDraw}
          aria-label={canDraw ? "Piocher un indice" : "La pioche est vide"}
        >
          <span className="dc-pioche__card" aria-hidden>
            <SearchRoundedIcon className="dc-pioche__card-icon" />
          </span>
        </button>
      )}

      {drawnCount > 0 && (
        <div className="dc-pioche__navigation">
          <button
            type="button"
            className="dc-pioche__arrow"
            onClick={onShowPrevious}
            disabled={!canShowPrevious}
            aria-label="Indice précédent"
          >
            <ChevronLeftRoundedIcon fontSize="inherit" />
          </button>

          <div className="dc-pioche__clue" aria-live="polite">
            {displayedClue && (
              <ClueCard
                key={`${displayedClue.id}-${clueNumber}`}
                title={`Indice ${clueNumber} / ${totalClues}`}
                description={displayedClue.descriptions[level]}
                isFlipping={!isLeaving}
                isLeaving={isLeaving}
              />
            )}
          </div>

          <button
            type="button"
            className="dc-pioche__arrow"
            onClick={onShowNext}
            disabled={!canShowNext}
            aria-label="Indice suivant"
          >
            <ChevronRightRoundedIcon fontSize="inherit" />
          </button>
        </div>
      )}
    </div>
  )
}

export default Pioche
